import type { MouseEventHandler } from "react";
import { useCallback, useRef } from "react";

type Handlers<T> = {
  click?: MouseEventHandler<T>;
  doubleClick?: MouseEventHandler<T>;
  wheelClick?: MouseEventHandler<T>;
};

/**
 * USE THIS VERY, VERY SPARINGLY
 *
 * With this, a click will only respond after 200ms. This is useful if you want to
 * have new behaviour on double click, and a single click should not execute if there
 * is a double click.
 *
 * Otherwise, NEVER use this, because it will make a click 200ms slower!
 */
export function useClickEvents<T extends HTMLElement>(
  { click, doubleClick, wheelClick }: Handlers<T>,
  timeout = 200,
): MouseEventHandler<T> {
  // we're using useRef here for the useCallback to rememeber the timeout
  const clickTimeout: {
    current: ReturnType<typeof setTimeout> | undefined;
  } = useRef();

  const clearClickTimeout = () => {
    if (clickTimeout) {
      clearTimeout(clickTimeout.current as ReturnType<typeof setTimeout>);
      clickTimeout.current = undefined;
    }
  };

  // return a memoized version of the callback that only changes if one of the dependencies has changed
  return useCallback(
    (event) => {
      clearClickTimeout();

      if (event.button === 1) {
        wheelClick?.(event);
        return;
      }

      if (click && event.detail === 1) {
        clickTimeout.current = setTimeout(() => {
          click(event);
        }, timeout);
      }

      if (event.detail % 2 === 0) {
        doubleClick?.(event);
      }
    },
    [click, doubleClick, timeout, wheelClick],
  );
}
